import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SEO } from '@/components/SEO';

interface BreadcrumbItem {
  label: string;
  path: string;
}

// Route to label mapping
const routeLabels: Record<string, string> = {
  '/': 'Home',
  '/aanbod': 'Aanbod',
  '/organisatieopstellingen': 'Organisatieopstellingen',
  '/familieopstellingen': 'Familieopstellingen',
  '/teamcoaching': 'Teamcoaching',
  '/1-op-1': '1-op-1',
  '/leergang-onderstroom': 'Leergang systemisch leiderschap',
  '/opleidingstrajecten': 'Opleidingstrajecten',
  '/over': 'Over',
  '/contact': 'Contact',
  '/agenda': 'Agenda',
  '/themadag': 'Themadag',
  '/summer-read': 'Summer Read',
  '/koop-strippenkaart': 'Koop Strippenkaart',
  '/algemene-voorwaarden': 'Algemene Voorwaarden',
  '/privacy-en-cookies': 'Privacy en Cookies',
};

interface BreadcrumbsProps {
  customItems?: BreadcrumbItem[];
  className?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ customItems, className }) => {
  const location = useLocation();
  const pathname = location.pathname;

  // Don't show breadcrumbs on homepage
  if (pathname === '/') {
    return null;
  }

  // Build breadcrumb items
  const buildBreadcrumbs = (): BreadcrumbItem[] => {
    if (customItems) {
      return customItems;
    }

    const items: BreadcrumbItem[] = [{ label: 'Home', path: '/' }];

    // Split pathname and build breadcrumbs
    const pathSegments = pathname.split('/').filter(Boolean);

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const label = routeLabels[currentPath] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      items.push({
        label,
        path: currentPath,
      });
    });

    return items;
  };

  const breadcrumbItems = buildBreadcrumbs();

  // Generate Schema.org structured data
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: `https://organisatieopstellingen.com${item.path}`,
    })),
  };

  return (
    <>
      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            ...breadcrumbSchema,
          }),
        }}
      />

      {/* Visual breadcrumbs */}
      <Breadcrumb className={className}>
        <BreadcrumbList>
          {breadcrumbItems.map((item, index) => (
            <React.Fragment key={item.path}>
              <BreadcrumbItem>
                {index === breadcrumbItems.length - 1 ? (
                  <BreadcrumbPage>{item.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={item.path}>{item.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {index < breadcrumbItems.length - 1 && <BreadcrumbSeparator />}
            </React.Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </>
  );
};

